import { DateTime } from 'luxon';
import { Customers, Drivers, Orders, Trailers, Trucks } from '../../interfaces';

export interface TruckContact {
  truckEmail: string;
  truckNumber: string;
}

/** Resolve truck inbox + label for one driver from master driver/truck lists. */
export function getTruckContactForDriver(
  driverId: number,
  drivers: Drivers[],
  trucks: Trucks[]
): TruckContact {
  const id = Number(driverId);
  const driver = drivers.find(d => Number(d.driver_id) === id);
  const truck = trucks.find(
    t => driver?.truck_id != null && Number(t.truck_id) === Number(driver.truck_id)
  );
  return {
    truckEmail: truck?.email?.trim() ? String(truck.email) : '',
    truckNumber: truck?.truck_number ? String(truck.truck_number) : 'No Truck Assigned',
  };
}

function getOrdinalWord(num: number): string {
  const ordinals = [
    'First', 'Second', 'Third', 'Fourth', 'Fifth',
    'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth',
  ];
  return ordinals[num - 1];
}

function getCustomerName(customers: Customers[], customerId: number): string {
  const customer = customers.find(c => c.customer_id === customerId);
  return customer?.name || 'Unknown';
}

function getTrailerNumber(trailers: Trailers[], trailerId: number | null): string {
  if (!trailerId) return 'None Assigned';
  const trailer = trailers.find(t => t.trailer_id === trailerId);
  return trailer?.trailer_number || 'None Assigned';
}

function toCentralTime(date: string | Date): string {
  const dt = DateTime.fromISO(date.toString(), { zone: 'utc' });
  const centralTime = dt.setZone('America/Chicago');
  if (centralTime.hour === 0 && centralTime.minute === 0 && centralTime.second === 0) {
    return centralTime.toFormat('MM/dd/yyyy');
  }
  return centralTime.toFormat('MM/dd/yyyy h:mm a');
}

function sortDispatchOrdersByPriority<T extends { priority?: number | string }>(dispatchOrders: T[]): T[] {
  return [...dispatchOrders].sort((a, b) => {
    const priorityA = typeof a.priority === 'number' ? a.priority : parseInt(String(a.priority), 10) || 0;
    const priorityB = typeof b.priority === 'number' ? b.priority : parseInt(String(b.priority), 10) || 0;
    return priorityA - priorityB;
  });
}

/** Build plain-text dispatch email body for a driver card (dashboard: full card loads). */
export function formatDispatchOrdersForCard(
  card: { dispatchOrders: any[] },
  selectedDate: Date | null,
  orders: Orders[],
  customers: Customers[],
  trailers: Trailers[]
): string {
  const displayDate = selectedDate
    ? DateTime.fromJSDate(selectedDate).setZone('America/Chicago')
    : DateTime.now().setZone('America/Chicago');

  let text = `Dispatch for ${displayDate.toFormat('MM/dd/yyyy')}\n\n`;

  const sortedOrders = sortDispatchOrdersByPriority(card.dispatchOrders ?? []);

  sortedOrders.forEach((order: any, index: number) => {
    text += `${getOrdinalWord(index + 1)} Load:\n`;
    if (order.order_id === null) {
      text += `${order.notes}\n`;
      text += `Time: ${toCentralTime(order.scheduled_time)}\n`;
    } else {
      const orderInfo = orders.find(o => o.order_id === order.order_id);
      if (orderInfo) {
        text += `${getCustomerName(customers, orderInfo.supplier_id)} to ${getCustomerName(customers, orderInfo.receiver_id)}\n`;
        if (orderInfo.pickup_date) {
          const pickupDateTime = DateTime.fromISO(orderInfo.pickup_date.toString(), { zone: 'utc' });
          const pickupCentral = pickupDateTime.setZone('America/Chicago');
          if (
            pickupDateTime.isValid &&
            !(pickupCentral.hour === 0 && pickupCentral.minute === 0 && pickupCentral.second === 0)
          ) {
            text += `Pickup Time: ${pickupCentral.toFormat('h:mm a')}\n`;
          }
        }
        if (orderInfo.delivery_date) {
          const deliveryDateTime = DateTime.fromISO(orderInfo.delivery_date.toString(), { zone: 'utc' });
          const deliveryCentral = deliveryDateTime.setZone('America/Chicago');
          if (
            deliveryDateTime.isValid &&
            !(deliveryCentral.hour === 0 && deliveryCentral.minute === 0 && deliveryCentral.second === 0)
          ) {
            text += `Delivery Time: ${deliveryCentral.toFormat('h:mm a')}\n`;
          }
        }
        const trailerLabel = orderInfo.trailer_id
          ? getTrailerNumber(trailers, orderInfo.trailer_id)
          : orderInfo.external_trailer_number?.trim();
        if (trailerLabel) {
          text += `Trailer: ${trailerLabel}\n`;
        }
        if (orderInfo.pickup_number) {
          text += `Pickup Number: ${orderInfo.pickup_number}\n`;
        }
        if (orderInfo.notes) text += `Notes: ${orderInfo.notes}\n`;
      }
    }
    text += '\n';
  });

  return text;
}
